import { useQuery } from "@tanstack/react-query";
import type * as type from "@vTypes";
import type { NurseResponse } from "@/types/nurse-type";
import type { CurDate } from "@/store/scheduleStore";
import httpClient from "@/config/axios";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";

/**
 * 모든 간호사 희망사항 조회 응답 아이템
 * get_all_preferences_service의 리턴 구조
 */
export interface AllPreferenceItem {
  nurse_id: string;
  year: number;
  month: number;
  is_submitted: boolean;
  created_at: string; // ISO 8601 datetime
  submitted_at: string | null; // ISO 8601 datetime
  data: PreferenceDetails;
}

/**
 * 간호사별 상세 희망 데이터
 */
export interface PreferenceDetails {
  request: string; // 전체 요청 텍스트 (예: "주말은 데이로 주세요")
  /**
   * 근무별/날짜별 신청 정보
   * Record<근무코드, Record<날짜(1~31), 상세정보>>
   * 예: { "D": { "1": { "score": 1.0, "comment": "개인사유" } } }
   */
  shift: Record<string, Record<string, ShiftDayDetail>>;
  /**
   * 선호/비선호 동료 정보
   */
  preference: PairPreference[];
}

/**
 * 날짜별 근무 신청 상세
 */
export interface ShiftDayDetail {
  score: number; // 반영 가중치
  comment: string; // 신청 사유 (메시지)
}

/**
 * 동료 선호도 정보
 */
export interface PairPreference {
  id: string; // 대상 간호사 ID
  weight: number; // 선호 가중치 (양수: 선호, 음수: 비선호)
}

// 전체 리턴값은 이 아이템의 배열입니다.
// type AllPreferencesResponse = AllPreferenceItem[];

function preferenceToSchedule(
  arrayLength: number,
  originArray: AllPreferenceItem | undefined
) {
  const result = Array(arrayLength).fill(null);
  if (originArray && originArray.data.shift) {
    for (const [letter, dates] of Object.entries(originArray.data.shift)) {
      for (const day of Object.keys(dates).map(Number)) {
        result[day - 1] = letter;
      }
    }
  }
  return result;
}

export default function usePreferenceSchedule({
  isAdmin,
  selectedGroupId,
  curDate,
  fullNurseSchema,
}: {
  isAdmin: boolean;
  selectedGroupId: string | null;
  curDate: CurDate | null;
  fullNurseSchema: NurseResponse[];
}) {
  const gid = isAdmin ? (selectedGroupId ?? "") : "";
  const validGid = gid && gid.trim() !== "" && gid !== "null" ? gid : "";

  return useQuery<type.PreferenceSchedule[]>({
    queryKey: QUERY_KEYS.preferences.schedule(curDate?.year as number, curDate?.month as number, validGid),
    queryFn: async () => {
      if (!curDate?.year || !curDate?.month) {
        return [];
      }
      if (isAdmin && !validGid) {
        return [];
      }

      const hasValidGroupId = !!(
        validGid &&
        validGid.trim() !== "" &&
        validGid !== "null"
      );

      const base = `/preferences/all?year=${curDate.year}&month=${curDate.month}`;

      const url = hasValidGroupId
        ? `${base}&group_id=${encodeURIComponent(validGid!)}`
        : base;

      const res_prefer_all = await httpClient.get<AllPreferenceItem[]>(url);

      const arrayLength = new Date(curDate.year, curDate.month, 0).getDate();

      const fullNurse_preference = fullNurseSchema.map((nurse) => {
        const found = res_prefer_all.find((p) => p.nurse_id === nurse.nurse_id);

        // shift에서 comment 추출 (날짜별 메시지)
        const shiftMessages: Record<number, string> = {};
        if (found?.data?.shift) {
          for (const [, dates] of Object.entries(found.data.shift)) {
            for (const [dayStr, detail] of Object.entries(dates)) {
              const day = Number(dayStr);
              if (detail.comment) {
                shiftMessages[day] = detail.comment;
              }
            }
          }
        }

        return {
          nurse_id: nurse.nurse_id,
          schedule: preferenceToSchedule(arrayLength, found),
          request: found?.data?.request, // 전체 요청 텍스트
          shiftMessages, // 날짜별 메시지 (comment)
        };
      });

      return fullNurse_preference;
    },
    enabled:
      !!curDate?.year &&
      !!curDate?.month &&
      fullNurseSchema.length > 0 &&
      (!isAdmin || !!validGid),
    staleTime: 1000 * 60 * 5, // 5분간 캐시
  });
}
